import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import { displayGroupRole } from "../../../../application/domain/rbac/rbac";
import {
  MyAccountService,
  MyAccountServiceProps,
} from "../../../../application/service/smart-apply/users/my-account-service";
import { useAuthContextV2 } from "../../../../auth-state";
import { reportUnexpectedError } from "../../../../error-reporter";
import { useModal } from "../../../../utils/custom-hooks";
import { LinkButton } from "../../../atoms/link-button";
import { EditButtons } from "../../../molecules/group-buttons/edit-button";
import { ConfirmModal } from "../../../organisms/common/confirm-modal";
import { ResourceFetchError } from "../../../organisms/common/resource-fetch-error";
import { UserEmailModal } from "../../../organisms/settings/user-email-modal";
import { SettingsTemplate } from "../../../template/settings/default-template";
import { DeprecatedTooltip } from "../../../atoms/deprecated-tooltip";

export const MyAccountPage: React.FC<MyAccountServiceProps> = ({
  isLoading,
  user,
  userGroup,
  setUserId,
  fetchUser,
  issueEmailToken,
  verifyEmailToken,
  resetUserEmail,
}) => {
  const authState = useAuthContextV2();
  const userEmailModal = useModal(false);
  const deleteEmailConfirmModal = useModal(false);
  const onResetUserEmail = async () => {
    try {
      await resetUserEmail();
      toast.success("メールアドレスを解除しました。");
      deleteEmailConfirmModal.close();
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("メールアドレスの解除に失敗しました。");
    }
  };

  useEffect(() => {
    setUserId(authState.auth.data?.id!);
    fetchUser();
  }, [authState, setUserId, fetchUser]);

  return (
    <>
      <SettingsTemplate breadcrumbs={[{ to: "#", title: "個人設定" }]}>
        <h1 className="title">
          <i className="fa fa-user" /> アカウント
        </h1>
        {!isLoading && (
          <>
            {!user && (
              <ResourceFetchError
                title="ユーザーデータが存在しません"
                subtitle="ユーザーデータが存在しません"
              />
            )}
            {user && (
              <>
                <div className="columns">
                  <div className="column main is-6">
                    <h3 className="title is-size-4 mb-4">基本情報</h3>
                    <div className="field">
                      <table className="table">
                        <tbody>
                          <tr className="c-tr-has-tag">
                            <th>氏名</th>
                            <td>
                              <p>{user.name}</p>
                            </td>
                          </tr>
                          <tr className="c-tr-has-tag">
                            <th>アカウント名</th>
                            <td>
                              <p>{user.accountName}</p>
                            </td>
                          </tr>
                          <tr className="c-tr-has-tag">
                            <th>
                              メールアドレス
                              {!authState.auth.hasRole("Admin") && (
                                <DeprecatedTooltip
                                  text={`申請到達時に通知を受け付けるメールアドレスを設定することが出来ます。`}
                                />
                              )}
                            </th>
                            <td>
                              {user.email ? (
                                <>
                                  {user.email}{" "}
                                  <EditButtons
                                    onClickEdit={userEmailModal.open}
                                    onClickDelete={deleteEmailConfirmModal.open}
                                  />
                                </>
                              ) : (
                                <LinkButton onClick={userEmailModal.open}>
                                  登録する
                                </LinkButton>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                {userGroup && Object.keys(user.groupsRoles).length > 0 && (
                  <div className="columns">
                    <div className="column main is-6">
                      <h3 className="title is-size-4 mb-4">所属情報</h3>
                      <div className="field">
                        <table className="table is-fullwidth">
                          <thead>
                            <tr>
                              <th>グループ名称</th>
                              <th>ロール</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userGroup.map((g) => (
                              <tr key={g.id}>
                                <td>{g.name}</td>
                                <td>
                                  {displayGroupRole(user.groupsRoles[g.id!])}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </SettingsTemplate>
      <UserEmailModal
        isActive={userEmailModal.isActive}
        onModal={userEmailModal.close}
        issueEmailToken={issueEmailToken}
        verifyEmailToken={verifyEmailToken}
      />
      <ConfirmModal
        isActive={deleteEmailConfirmModal.isActive}
        title={"メールアドレスの登録解除"}
        body={"登録されているメールアドレスを解除します。よろしいでしょうか。"}
        successButtonText={"解除する"}
        onSuccess={onResetUserEmail}
        onClose={deleteEmailConfirmModal.close}
      />
    </>
  );
};

export const MyAccountContainer = connectPageService(
  MyAccountPage,
  () => new MyAccountService(),
);
